/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The week ribbon — the app's one piece of shared wayfinding.
 *
 * ── WHY IT IS SHARED ────────────────────────────────────────────────────────
 * Home rendered a seven-day density strip; Plan rendered a seven-day grid;
 * Shopping rendered nothing about the week at all, despite existing entirely to
 * buy for one. Three screens about the same seven days, with no common element
 * tying them together, so moving between them felt like moving between apps.
 *
 * This is that element: same shape, same position, on Today, Plan and Shopping.
 * It answers "where am I in the week" continuously, which is a job no amount of
 * nav labelling can do.
 *
 * ── DATES ───────────────────────────────────────────────────────────────────
 * Every date is passed in. `isToday` comes from the server's own comparison
 * (via /api/home or /api/plan), never from a client-side one, so the highlight
 * cannot disagree with the data it sits above. Nothing here constructs a date.
 */

import { useNavigate } from "react-router-dom";

import { formatWeekday, type YMD } from "../lib/dates.ts";
import { usePrefs } from "../i18n/index.tsx";

export interface RibbonDay {
  date: YMD;
  /** Planned meals on this day. */
  count: number;
  isToday: boolean;
}

export function WeekRibbon({ days, activeDate, onPick, className = "" }: {
  days: RibbonDay[];
  /** Ringed rather than filled — "the day you're looking at", not "today". */
  activeDate?: YMD;
  /** Defaults to navigating to Plan focused on that day. */
  onPick?: (date: YMD) => void;
  className?: string;
}) {
  const navigate = useNavigate();
  const { lang } = usePrefs();

  if (days.length === 0) return null;

  const pick = (date: YMD) => {
    if (onPick) onPick(date);
    else navigate(`/plan?date=${date}`);
  };

  return (
    <nav aria-label="This week" className={`no-print ${className}`}>
      <ol className="grid grid-cols-7 gap-1">
        {days.map((d) => {
          const active = d.date === activeDate;
          return (
            <li key={d.date}>
              <button
                type="button"
                onClick={() => pick(d.date)}
                aria-current={d.isToday ? "date" : undefined}
                // The count is in the accessible name because "3" alone tells a
                // screen-reader user nothing about what three there are of.
                aria-label={`${formatWeekday(d.date, lang)} — ${
                  d.count === 0 ? "nothing planned" : `${d.count} planned`}`}
                className={`flex h-full w-full min-h-[3.25rem] flex-col items-center justify-center
                            rounded-lg border py-1.5 transition ${
                  d.isToday
                    ? "border-accent bg-accent/5"
                    : "border-rule-soft hover:border-accent/50"
                } ${active ? "ring-1 ring-accent" : ""}`}
              >
                <span className={`label-mono text-[10px] ${
                  d.isToday ? "text-accent" : "text-ink-3"}`}>
                  {formatWeekday(d.date, lang)}
                </span>
                <span className={`text-base leading-tight ${
                  d.count === 0 ? "text-ink-3" : "text-ink"
                } ${d.isToday ? "font-semibold" : ""}`}>
                  {d.count || "–"}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
